/**
 * CustomerAccountModal
 * Shown from the terminal DocViewer when a credit invoice is scanned
 * and the doc has a registered customer_id.
 *
 * 3 tabs:
 *   1. Open Invoices   — all unpaid/partial, oldest first
 *   2. History         — invoices + payments for a configurable period, newest first
 *   3. Payments        — payments only for a configurable period, newest first
 *
 * Each tab has a "Print (Thermal)" button.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Printer, RefreshCw, User, Wallet, AlertTriangle, Check, Minus, Plus } from 'lucide-react';
import axios from 'axios';
import { api as mainApi } from '../contexts/AuthContext';
import PrintBridge from '../lib/PrintBridge';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const PRINT_LABELS = {
  open_invoices: 'Open Invoices',
  history: 'History',
  payments: 'Payments',
};

const PERIOD_OPTIONS = [
  { value: '7d',  label: 'Last 7 Days'   },
  { value: '30d', label: 'Last 30 Days'  },
  { value: '90d', label: 'Last 3 Months' },
  { value: 'all', label: 'All Time'      },
];

function php(v) {
  return `₱${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });
  } catch { return iso?.slice(0, 10) || '—'; }
}

function StatusBadge({ status, balance }) {
  const b = parseFloat(balance) || 0;
  if (b <= 0 || status === 'paid') return <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Paid</Badge>;
  if (status === 'partial') return <Badge className="bg-amber-100 text-amber-800 text-[10px]">Partial</Badge>;
  return <Badge className="bg-red-100 text-red-800 text-[10px]">Open</Badge>;
}

export default function CustomerAccountModal({ customerId, customerName, open, onClose, businessInfo, token, branchId }) {
  const [tab,    setTab]    = useState('open');
  const [period, setPeriod] = useState('30d');
  const [data,   setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  // If a terminal token is supplied, create a one-off axios instance with it.
  // Otherwise fall back to the main AuthContext api (normal web app flow).
  const api = useMemo(() => {
    if (!token) return mainApi;
    const inst = axios.create({ baseURL: `${BACKEND}/api` });
    inst.interceptors.request.use(cfg => {
      cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return inst;
  }, [token]);

  // Inline collect-payment state (on Open Invoices tab)
  const [collectTarget, setCollectTarget] = useState(null); // invoice to pay
  const [collectPin,    setCollectPin]    = useState('');
  const [collectAmt,    setCollectAmt]    = useState('');
  const [collectMethod, setCollectMethod] = useState('Cash');
  const [collectStep,   setCollectStep]   = useState('pin'); // 'pin' | 'amount' | 'confirm' | 'done'
  const [collectBusy,   setCollectBusy]   = useState('');
  const [collectErr,    setCollectErr]    = useState('');

  // Print state — copy-count picker
  const [printTarget, setPrintTarget] = useState(null); // { printType, copies }
  const [printing, setPrinting] = useState(false);
  const handlePrint = async () => {
    if (!data || !printTarget) return;
    setPrinting(true);
    try {
      for (let i = 0; i < (printTarget.copies || 1); i++) {
        await PrintBridge.printStatement(data, businessInfo, printTarget.printType);
        if (i < (printTarget.copies || 1) - 1) {
          await new Promise(r => setTimeout(r, 600)); // small gap between copies
        }
      }
      toast.success(`Printed ${printTarget.copies} copy(s)`);
    } catch (e) {
      toast.error(e.message || 'Print failed');
    } finally {
      setPrinting(false);
      setPrintTarget(null);
    }
  };

  const load = useCallback(async () => {
    if (!customerId || !open) return;
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/account-summary`, {
        params: { period },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [customerId, period, open]);

  useEffect(() => { load(); }, [load]);

  const startCollect = (inv) => {
    setCollectTarget(inv);
    setCollectAmt(String(parseFloat(inv.balance).toFixed(2)));
    setCollectMethod('Cash');
    setCollectPin('');
    setCollectStep('pin');
    setCollectErr('');
  };

  const verifyCollectPin = async () => {
    if (!collectPin || collectPin.length < 4) { setCollectErr('Enter PIN or TOTP code'); return; }
    setCollectBusy('Verifying…');
    setCollectErr('');
    try {
      const res = await api.post('/verify/verify-pin-action', {
        pin: collectPin,
        action: 'receive_customer_payment',
      });
      if (res.data?.verified || res.data?.verifier_id) {
        setCollectStep('amount');
      } else {
        setCollectErr('PIN not accepted');
      }
    } catch (e) {
      setCollectErr(e.response?.data?.detail || 'Invalid PIN');
    } finally {
      setCollectBusy('');
      setCollectPin('');
    }
  };

  const submitCollect = async () => {
    if (!parseFloat(collectAmt) > 0) { setCollectErr('Enter valid amount'); return; }
    setCollectBusy('Processing…');
    setCollectErr('');
    try {
      await api.post(`/customers/${customerId}/receive-payment`, {
        allocations: [{ invoice_id: collectTarget.invoice_id, amount: parseFloat(collectAmt) }],
        method: collectMethod,
        // branch_id arms the server's closed-day/date guards (audit F-41) —
        // callers that know the branch pass it; legacy callers unchanged.
        ...(branchId ? { branch_id: branchId } : {}),
        idempotency_key: `acct-modal-${collectTarget.invoice_id}-${Date.now()}`,
      });
      setCollectStep('done');
      load(); // refresh data
      toast.success(`Payment of ${php(parseFloat(collectAmt))} recorded`);
    } catch (e) {
      setCollectErr(e.response?.data?.detail || 'Payment failed');
      setCollectStep('amount');
    } finally {
      setCollectBusy('');
    }
  };

  const openBalance = data?.open_balance ?? 0;
  const checkLimbo   = data?.bounced_check_limbo ?? null;
  const openInvoices = data?.open_invoices ?? [];
  const history      = data?.history ?? [];
  const payments     = data?.payments_only ?? [];
  const customer     = data?.customer ?? {};

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <User size={16} className="text-emerald-700" />
            {customerName || customer.name || 'Customer Account'}
          </DialogTitle>
          {customer.phone && (
            <p className="text-xs text-slate-500 mt-0.5">{customer.phone}</p>
          )}
        </DialogHeader>

        {/* Open balance hero */}
        <div className="px-5 py-3 bg-red-50 border-b shrink-0 flex items-center justify-between" data-testid="customer-open-balance">
          <div>
            <p className="text-xs text-slate-500">Open Balance</p>
            <p className="text-xl font-bold text-red-700">{php(openBalance)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">{openInvoices.length} open invoice(s)</p>
            {loading && <RefreshCw size={14} className="animate-spin text-slate-400 ml-auto mt-1" />}
          </div>
        </div>

        {/* Bounced-check limbo — these invoices read as PAID while the bank may
            still refuse the money. Shown so nobody extends credit blind. */}
        {checkLimbo?.count > 0 && (
          <div className="px-5 py-2 bg-amber-50 border-b shrink-0" data-testid="customer-check-limbo">
            <p className="text-xs font-semibold text-amber-800">
              {php(checkLimbo.total)} in bounced checks awaiting redeposit
            </p>
            <p className="text-[11px] text-amber-600 mt-0.5">
              {checkLimbo.checks.map(c => `#${c.check_number}${c.attempts > 1 ? ` (${c.attempts} tries)` : ''}`).join(' · ')}
              {' — '}counted as paid until the bank clears or it is marked final.
            </p>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="mx-5 mt-3 shrink-0 grid grid-cols-3 h-8">
            <TabsTrigger value="open"     data-testid="acct-tab-open"     className="text-xs">Open Invoices</TabsTrigger>
            <TabsTrigger value="history"  data-testid="acct-tab-history"  className="text-xs">Inv &amp; Payments</TabsTrigger>
            <TabsTrigger value="payments" data-testid="acct-tab-payments" className="text-xs">Payments</TabsTrigger>
          </TabsList>

          {/* ── Period picker (shown for history + payments) ── */}
          {(tab === 'history' || tab === 'payments') && (
            <div className="flex items-center gap-2 px-5 pt-2 shrink-0">
              <span className="text-xs text-slate-500">Period:</span>
              <Select value={period} onValueChange={v => { setPeriod(v); }}>
                <SelectTrigger className="h-7 text-xs w-36" data-testid="acct-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── TAB: Open Invoices ── */}
          <TabsContent value="open" className="flex-1 overflow-y-auto px-5 pb-3 mt-2 space-y-2">
            {loading && <p className="text-center text-xs text-slate-400 py-8">Loading…</p>}
            {!loading && !openInvoices.length && (
              <div className="text-center py-8">
                <p className="text-emerald-600 font-semibold text-sm">No open invoices</p>
                <p className="text-xs text-slate-400 mt-1">This customer is fully paid up.</p>
              </div>
            )}

            {/* Inline collect-payment panel */}
            {collectTarget && (
              <div className="border-2 border-emerald-300 rounded-xl p-3 bg-emerald-50 space-y-2" data-testid="collect-payment-panel">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-emerald-800">
                    <Wallet size={12} className="inline mr-1" />
                    Collect Payment — {collectTarget.invoice_number}
                  </p>
                  <button onClick={() => setCollectTarget(null)} className="text-slate-400 hover:text-red-500">
                    <span className="text-xs">✕</span>
                  </button>
                </div>

                {collectStep === 'done' ? (
                  <div className="text-center py-2">
                    <Check size={20} className="text-emerald-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-emerald-700">Payment recorded!</p>
                    <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => setCollectTarget(null)}>Close</Button>
                  </div>
                ) : collectStep === 'pin' ? (
                  <div className="flex gap-2">
                    <input type="password" inputMode="numeric" maxLength={6}
                      value={collectPin} onChange={e => { setCollectPin(e.target.value); setCollectErr(''); }}
                      onKeyDown={e => e.key === 'Enter' && verifyCollectPin()}
                      placeholder="Manager / Admin PIN"
                      className="flex-1 h-8 px-3 text-xs font-mono text-center rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid="collect-pin-input"
                      autoComplete="one-time-code"
                    />
                    <Button size="sm" onClick={verifyCollectPin} disabled={!!collectBusy || collectPin.length < 4}
                      className="h-8 text-xs bg-emerald-700 text-white" data-testid="collect-pin-verify-btn">
                      {collectBusy ? '…' : 'Verify'}
                    </Button>
                  </div>
                ) : collectStep === 'amount' ? (
                  <div className="space-y-2">
                    <div className="flex gap-1.5">
                      {['Cash','GCash','Maya','Check'].map(m => (
                        <button key={m} onClick={() => setCollectMethod(m)}
                          className={`px-2 py-1 rounded-full text-[10px] font-medium ${collectMethod===m ? 'bg-emerald-700 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="number" inputMode="decimal" min={0} max={collectTarget.balance}
                        value={collectAmt} onChange={e => setCollectAmt(e.target.value)} onFocus={e => e.target.select()}
                        className="flex-1 h-9 px-3 text-sm font-mono text-right rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        data-testid="collect-amount-input"
                      />
                      <Button size="sm" variant="outline" className="h-9 text-xs"
                        onClick={() => setCollectAmt(String(parseFloat(collectTarget.balance).toFixed(2)))}>
                        Full
                      </Button>
                      <Button size="sm" onClick={() => { setCollectErr(''); setCollectStep('confirm'); }}
                        disabled={!(parseFloat(collectAmt) > 0)}
                        className="h-9 text-xs bg-emerald-700 text-white" data-testid="collect-review-btn">
                        Review
                      </Button>
                    </div>
                  </div>
                ) : collectStep === 'confirm' ? (
                  <div className="space-y-2">
                    <div className="bg-white border border-emerald-200 rounded-lg px-3 py-2 text-xs space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold text-emerald-800">{php(parseFloat(collectAmt))}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Method</span><span>{collectMethod}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span>{collectTarget.invoice_number}</span></div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 h-9 text-xs" onClick={() => setCollectStep('amount')}>Edit</Button>
                      <Button size="sm" onClick={submitCollect} disabled={!!collectBusy}
                        className="flex-1 h-9 text-xs bg-emerald-700 text-white" data-testid="collect-confirm-btn">
                        {collectBusy || 'Confirm Payment'}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {collectErr && <p className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle size={10}/>{collectErr}</p>}
              </div>
            )}

            {!loading && openInvoices.map(inv => (
              <div key={inv.invoice_id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`open-inv-${inv.invoice_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-sm">{inv.invoice_number}</p>
                    <p className="text-xs text-slate-500">{fmtDate(inv.date)}{inv.days_overdue > 0 ? <span className="text-red-600 font-semibold ml-1">+{inv.days_overdue}d overdue</span> : null}</p>
                    {inv.items_preview && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[220px]">{inv.items_preview}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={inv.status} balance={inv.balance} />
                    <p className="text-sm font-bold text-red-700 mt-1">{php(inv.balance)}</p>
                    <p className="text-[10px] text-slate-400">of {php(inv.grand_total)}</p>
                    {!collectTarget && (
                      <Button size="sm" variant="outline"
                        className="mt-1.5 h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => startCollect(inv)}
                        data-testid={`collect-btn-${inv.invoice_id}`}
                      >
                        <Wallet size={10} className="mr-1" /> Collect
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── TAB: Invoice & Payment History ── */}
          <TabsContent value="history" className="flex-1 overflow-y-auto px-5 pb-3 mt-2 space-y-1.5">
            {loading && <p className="text-center text-xs text-slate-400 py-8">Loading…</p>}
            {!loading && !history.length && (
              <p className="text-center text-xs text-slate-400 py-8">No transactions in this period.</p>
            )}
            {!loading && history.map((row, i) => (
              row.type === 'invoice' ? (
                <div key={`inv-${row.invoice_id}-${i}`} className="border border-slate-200 rounded-lg p-2.5 bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-xs">{row.invoice_number} <span className="font-normal text-slate-400">{row.invoice_type !== 'Invoice' ? `· ${row.invoice_type}` : ''}</span></p>
                      <p className="text-[11px] text-slate-500">{fmtDate(row.date)}</p>
                      {row.items_preview && <p className="text-[11px] text-slate-400 truncate max-w-[200px]">{row.items_preview}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold">{php(row.grand_total)}</p>
                      <StatusBadge status={row.status} balance={row.balance} />
                      {parseFloat(row.balance) > 0 && <p className="text-[10px] text-red-600">Due: {php(row.balance)}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                <div key={`pay-${i}`} className="border border-emerald-100 rounded-lg p-2.5 bg-emerald-50/40 flex items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-emerald-800">Payment · {row.method}</p>
                    <p className="text-[11px] text-slate-500">{fmtDate(row.date)} · {row.invoice_number}</p>
                    {(row.receipt_number || row.reference) && <p className="text-[11px] text-slate-400">Ref: {row.receipt_number || row.reference}</p>}
                  </div>
                  <p className="text-sm font-bold text-emerald-700 shrink-0">-{php(row.amount)}</p>
                </div>
              )
            ))}
          </TabsContent>

          {/* ── TAB: Payments ── */}
          <TabsContent value="payments" className="flex-1 overflow-y-auto px-5 pb-3 mt-2 space-y-1.5">
            {loading && <p className="text-center text-xs text-slate-400 py-8">Loading…</p>}
            {!loading && !payments.length && (
              <p className="text-center text-xs text-slate-400 py-8">No payments in this period.</p>
            )}
            {!loading && payments.map((p, i) => (
              <div key={i} className="border border-emerald-100 rounded-lg p-2.5 bg-emerald-50/40 flex items-center justify-between gap-2" data-testid={`payment-row-${i}`}>
                <div>
                  <p className="text-xs font-semibold">{php(p.amount)} · {p.method}</p>
                  <p className="text-[11px] text-slate-500">{fmtDate(p.date)} · Invoice: {p.invoice_number}</p>
                  {(p.receipt_number || p.reference) && <p className="text-[11px] text-slate-400">Ref: {p.receipt_number || p.reference}</p>}
                </div>
                <p className="text-emerald-700 font-bold text-sm shrink-0">{php(p.amount)}</p>
              </div>
            ))}
            {!loading && payments.length > 0 && (
              <div className="border-t pt-2 flex justify-between text-xs font-semibold">
                <span>{payments.length} payment(s)</span>
                <span>{php(payments.reduce((s,p) => s + (parseFloat(p.amount)||0), 0))}</span>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ── Print Footer ── */}
        <div className="px-5 py-3 border-t bg-slate-50 shrink-0">
          {/* Copy-count picker — shown when a print button is tapped */}
          {printTarget ? (
            <div className="flex items-center gap-3 justify-between">
              <span className="text-xs text-slate-600 font-medium">
                {PRINT_LABELS[printTarget.printType]} — how many copies?
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="w-7 h-7 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  onClick={() => setPrintTarget(p => ({ ...p, copies: Math.max(1, p.copies - 1) }))}
                  disabled={printTarget.copies <= 1}
                  data-testid="print-copies-minus"
                >
                  <Minus size={13} />
                </button>
                <span className="w-6 text-center text-sm font-bold tabular-nums">{printTarget.copies}</span>
                <button
                  className="w-7 h-7 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  onClick={() => setPrintTarget(p => ({ ...p, copies: Math.min(5, p.copies + 1) }))}
                  disabled={printTarget.copies >= 5}
                  data-testid="print-copies-plus"
                >
                  <Plus size={13} />
                </button>
                <Button size="sm" className="bg-[#1A4D2E] text-white h-7 text-xs ml-1"
                  onClick={handlePrint} disabled={printing}
                  data-testid="print-confirm-btn">
                  {printing
                    ? <RefreshCw size={11} className="animate-spin mr-1" />
                    : <Printer size={11} className="mr-1" />
                  }
                  {printing ? 'Printing…' : 'Print'}
                </Button>
                <button
                  className="text-xs text-slate-400 hover:text-slate-600 ml-1"
                  onClick={() => setPrintTarget(null)}
                  data-testid="print-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Print (Thermal):</span>
              <div className="flex gap-2 flex-wrap justify-end">
                <Button size="sm" variant="outline" className="text-xs h-7"
                  onClick={() => setPrintTarget({ printType: 'open_invoices', copies: 1 })}
                  data-testid="print-open-invoices-btn"
                  disabled={!data}
                >
                  <Printer size={12} className="mr-1" /> Open Invoices
                </Button>
                <Button size="sm" variant="outline" className="text-xs h-7"
                  onClick={() => setPrintTarget({ printType: 'history', copies: 1 })}
                  data-testid="print-history-btn"
                  disabled={!data}
                >
                  <Printer size={12} className="mr-1" /> History
                </Button>
                <Button size="sm" variant="outline" className="text-xs h-7"
                  onClick={() => setPrintTarget({ printType: 'payments', copies: 1 })}
                  data-testid="print-payments-btn"
                  disabled={!data}
                >
                  <Printer size={12} className="mr-1" /> Payments
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
